import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  STAGE1_INBOX_CATEGORIES,
  STAGE1_INBOX_EMAILS,
  validateInboxTriage,
} from "./stage1InboxData";
import {
  clearEmptyDragImage,
  mountDragVisual,
  moveDragVisual,
  setEmptyDragImage,
  unmountDragVisual,
} from "./stage1DragVisual";

const INCOMING_VISIBLE_COUNT = 5;
const INCOMING_VISIBLE_COUNT_MOBILE = 3;
const UNREAD_MAIL_COUNT = 260;

function findDropzoneAt(clientX, clientY) {
  const el = document.elementFromPoint(clientX, clientY);
  const zone = el?.closest("[data-dropzone]");
  return zone?.dataset.dropzone || null;
}

function EmailCard({
  email,
  compact = false,
  isDragging = false,
  usePointerDrag = false,
  onDragStart,
  onDragEnd,
  onPointerDragStart,
}) {
  const handleDragStart = (e) => {
    if (usePointerDrag) return;
    setEmptyDragImage(e);
    mountDragVisual(e.currentTarget, e.clientX, e.clientY, {
      floatClass: "stage1-email__drag-float",
      draggingClass: "stage1-email--is-dragging",
    });
    e.dataTransfer.setData("text/inbox-email-id", email.id);
    e.dataTransfer.effectAllowed = "move";
    onDragStart?.(email.id);
  };

  const handleDrag = (e) => {
    if (usePointerDrag) return;
    moveDragVisual(e.clientX, e.clientY);
  };

  const handleDragEnd = (e) => {
    if (usePointerDrag) return;
    unmountDragVisual();
    onDragEnd?.(e);
  };

  const handlePointerDown = (e) => {
    if (!usePointerDrag || !e.isPrimary) return;
    e.preventDefault();
    e.stopPropagation();
    onPointerDragStart?.(email.id, e.clientX, e.clientY, e.currentTarget);
  };

  return (
    <div
      className={`stage1-email${compact ? " stage1-email--compact" : ""}${
        isDragging ? " stage1-email--is-dragging" : ""
      }`}
      draggable={!usePointerDrag}
      onDragStart={handleDragStart}
      onDrag={handleDrag}
      onDragEnd={handleDragEnd}
      onPointerDown={handlePointerDown}
    >
      <span
        className="stage1-email__icon"
        style={{ backgroundColor: email.iconBg }}
        aria-hidden
      >
        {email.icon}
      </span>
      <span className="stage1-email__subject">{email.subject}</span>
      {!compact ? (
        <span className="stage1-email__grip" aria-hidden>
          ⠿
        </span>
      ) : null}
      <span className="stage1-email__sender">{email.sender}</span>
      <span className="stage1-email__time">{email.time}</span>
    </div>
  );
}

function CategoryTray({
  category,
  emails,
  draggingId,
  usePointerDrag,
  onDrop,
  onDragOver,
  onDragStart,
  onDragEnd,
  onPointerDragStart,
}) {
  return (
    <div className="stage1-inbox__tray-wrap">
      <div
        className="stage1-inbox__tray-label"
        style={{ color: category.color }}
      >
        <span className="stage1-inbox__tray-label-icon" aria-hidden>
          {category.id === "action_now" && "⏰"}
          {category.id === "reply_later" && "⏰"}
          {category.id === "archive" && "📦"}
          {category.id === "ignore" && "✕"}
        </span>
        {category.shortLabel}
      </div>
      <div
        className={`stage1-inbox__tray${
          emails.length ? " stage1-inbox__tray--filled" : ""
        }`}
        data-dropzone={category.id}
        onDragOver={onDragOver}
        onDrop={(e) => onDrop(category.id, e)}
      >
        <img
          src={category.trayImg}
          alt=""
          className="stage1-inbox__tray-img"
          draggable={false}
        />
        <div
          className={`stage1-inbox__tray-well${
            emails.length ? " stage1-inbox__tray-well--filled" : ""
          }`}
        >
          {emails.length ? (
            <div className="stage1-inbox__tray-emails">
              {emails.map((email) => (
                <EmailCard
                  key={email.id}
                  email={email}
                  compact
                  isDragging={draggingId === email.id}
                  usePointerDrag={usePointerDrag}
                  onDragStart={onDragStart}
                  onDragEnd={onDragEnd}
                  onPointerDragStart={onPointerDragStart}
                />
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default function Stage1InboxTriage({
  onSubmit,
  submitting = false,
  isMobilePortrait = false,
}) {
  const [assignments, setAssignments] = useState({});
  const [draggingId, setDraggingId] = useState(null);
  const [activeDropzone, setActiveDropzone] = useState(null);
  const pointerDragCleanupRef = useRef(null);
  const advancedRef = useRef(false);

  const usePointerDrag = useMemo(() => {
    if (isMobilePortrait) return true;
    if (typeof window === "undefined") return false;
    return "ontouchstart" in window && window.innerWidth <= 900;
  }, [isMobilePortrait]);

  const poolEmails = useMemo(
    () => STAGE1_INBOX_EMAILS.filter((e) => !assignments[e.id]),
    [assignments]
  );

  const incomingVisibleCount = isMobilePortrait
    ? INCOMING_VISIBLE_COUNT_MOBILE
    : INCOMING_VISIBLE_COUNT;

  const visiblePoolEmails = useMemo(
    () => poolEmails.slice(0, incomingVisibleCount),
    [poolEmails, incomingVisibleCount]
  );

  const emailGridCols = 1;
  const emailGridRows = incomingVisibleCount;

  const emailsByCategory = useMemo(() => {
    const map = Object.fromEntries(
      STAGE1_INBOX_CATEGORIES.map((c) => [c.id, []])
    );
    STAGE1_INBOX_EMAILS.forEach((email) => {
      const cat = assignments[email.id];
      if (cat && map[cat]) map[cat].push(email);
    });
    return map;
  }, [assignments]);

  const remainingUnreadCount = UNREAD_MAIL_COUNT - Object.keys(assignments).length;

  const finishDrag = useCallback(() => {
    pointerDragCleanupRef.current?.();
    pointerDragCleanupRef.current = null;
    clearEmptyDragImage();
    unmountDragVisual();
    setDraggingId(null);
    setActiveDropzone(null);
  }, []);

  const placeEmail = useCallback((emailId, dropzoneId) => {
    if (!emailId || !dropzoneId) return;

    if (dropzoneId === "pool") {
      setAssignments((prev) => {
        const next = { ...prev };
        delete next[emailId];
        return next;
      });
      return;
    }

    const email = STAGE1_INBOX_EMAILS.find((item) => item.id === emailId);
    if (!email) return;

    if (email.correctCategory !== dropzoneId) {
      return;
    }

    setAssignments((prev) => ({ ...prev, [emailId]: dropzoneId }));
  }, []);

  const allowDrop = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }, []);

  const handleDragStart = useCallback((emailId) => {
    setActiveDropzone(null);
    window.requestAnimationFrame(() => setDraggingId(emailId));
  }, []);

  const handleDragEnd = useCallback(() => {
    finishDrag();
  }, [finishDrag]);

  const handleDropOnCategory = useCallback(
    (categoryId, e) => {
      e.preventDefault();
      const emailId = e.dataTransfer.getData("text/inbox-email-id");
      if (!emailId) return;
      placeEmail(emailId, categoryId);
      finishDrag();
    },
    [finishDrag, placeEmail]
  );

  const handleDropOnPool = useCallback(
    (e) => {
      e.preventDefault();
      const emailId = e.dataTransfer.getData("text/inbox-email-id");
      if (!emailId) return;
      placeEmail(emailId, "pool");
      finishDrag();
    },
    [finishDrag, placeEmail]
  );

  const handleDragOverCategory = useCallback((categoryId, e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setActiveDropzone(categoryId);
    moveDragVisual(e.clientX, e.clientY);
  }, []);

  const handleDragOverPool = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setActiveDropzone("pool");
    moveDragVisual(e.clientX, e.clientY);
  }, []);

  const handlePointerDragStart = useCallback(
    (emailId, clientX, clientY, sourceEl) => {
      if (draggingId) return;

      mountDragVisual(sourceEl, clientX, clientY, {
        floatClass: "stage1-email__drag-float",
        draggingClass: "stage1-email--is-dragging",
      });
      setActiveDropzone(null);
      setDraggingId(emailId);

      const onMove = (e) => {
        if (!e.isPrimary) return;
        e.preventDefault();
        moveDragVisual(e.clientX, e.clientY);
        setActiveDropzone(findDropzoneAt(e.clientX, e.clientY));
      };

      const onEnd = (e) => {
        if (!e.isPrimary) return;
        const dropzoneId = findDropzoneAt(e.clientX, e.clientY);
        if (dropzoneId) {
          placeEmail(emailId, dropzoneId);
        }
        finishDrag();
      };

      document.addEventListener("pointermove", onMove, { passive: false });
      document.addEventListener("pointerup", onEnd);
      document.addEventListener("pointercancel", onEnd);

      pointerDragCleanupRef.current = () => {
        document.removeEventListener("pointermove", onMove);
        document.removeEventListener("pointerup", onEnd);
        document.removeEventListener("pointercancel", onEnd);
      };
    },
    [draggingId, finishDrag, placeEmail]
  );

  useEffect(() => {
    if (!draggingId || usePointerDrag) return undefined;

    const onDragOver = (e) => {
      e.preventDefault();
      moveDragVisual(e.clientX, e.clientY);
    };

    document.addEventListener("dragover", onDragOver);
    return () => document.removeEventListener("dragover", onDragOver);
  }, [draggingId, usePointerDrag]);

  useEffect(() => {
    return () => {
      pointerDragCleanupRef.current?.();
      unmountDragVisual();
    };
  }, []);

  useEffect(() => {
    if (submitting || advancedRef.current) return;
    const result = validateInboxTriage(assignments);
    if (!result.allPlaced || !result.valid) return;

    advancedRef.current = true;
    onSubmit?.(assignments);
  }, [assignments, onSubmit, submitting]);

  return (
    <div
      className={`stage1-inbox${
        draggingId ? " stage1-inbox--dragging" : ""
      }${isMobilePortrait ? " stage1-inbox--mobile-portrait" : ""}`}
    >
      <header className="stage1-inbox__header">
        <h2 className="stage1-inbox__title">EMAIL CLASSIFICATION</h2>
        <p className="stage1-inbox__subtitle">
          {isMobilePortrait
            ? "Drag each email to the right category"
            : "Drag and drop each email to the correct category"}
        </p>
      </header>

      <div className="stage1-inbox__body">
        <div className="stage1-inbox__incoming">
          <div className="stage1-inbox__panel-head stage1-inbox__panel-head--blue">
            <span className="stage1-inbox__panel-icon" aria-hidden>
              ✉️
            </span>
            <span>Unread Mails</span>
            <span className="stage1-inbox__badge">{remainingUnreadCount}</span>
          </div>
          <div
            className="stage1-inbox__email-grid"
            style={{
              "--email-cols": emailGridCols,
              "--email-rows": emailGridRows,
            }}
            data-dropzone="pool"
            onDragOver={handleDragOverPool}
            onDrop={handleDropOnPool}
          >
            {visiblePoolEmails.map((email) => (
              <EmailCard
                key={email.id}
                email={email}
                isDragging={draggingId === email.id}
                usePointerDrag={usePointerDrag}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onPointerDragStart={handlePointerDragStart}
              />
            ))}
          </div>
        </div>

        <div className="stage1-inbox__categories">
          <div className="stage1-inbox__panel-head stage1-inbox__panel-head--light">
            <span className="stage1-inbox__panel-icon" aria-hidden>
              ⚙️
            </span>
            <span>Sort Into Categories</span>
          </div>
          <div className="stage1-inbox__tray-grid">
            {STAGE1_INBOX_CATEGORIES.map((category) => (
              <CategoryTray
                key={category.id}
                category={category}
                emails={emailsByCategory[category.id]}
                draggingId={draggingId}
                usePointerDrag={usePointerDrag}
                onDrop={handleDropOnCategory}
                onDragOver={(e) => handleDragOverCategory(category.id, e)}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onPointerDragStart={handlePointerDragStart}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
